import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/resend-access-link`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Backend always returns 200 with generic message — only true network errors land here.
      setError("We couldn't reach the server. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050A07] text-white flex flex-col" data-testid="signin-page">
      <header className="border-b border-[#1A2E22] bg-[#050A07]/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="signin-logo" aria-label="Delta GreenX home">
            <BrandLogo as="div" size="md" />
          </Link>
          <Link to="/" className="text-sm text-[#A1B2A8] hover:text-white inline-flex items-center gap-1.5" data-testid="signin-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-5 sm:px-6 py-12 sm:py-16">
        <div className="w-full max-w-[460px]">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#22C55E] font-semibold mb-3">
            Returning Applicant
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-3">
            Sign in to your <span className="text-[#22C55E]">demo</span>.
          </h1>
          <p className="text-[#A1B2A8] text-sm leading-relaxed mb-8">
            Already applied? Enter your registered email and we'll send your magic access link.
            Once admin approves your application, the same link drops you straight into your
            personalised dashboard — no password needed.
          </p>

          {!sent ? (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="signin-form">
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#A1B2A8] mb-2 font-semibold">
                  Registered email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5D7A6B]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full bg-[#09120D] border border-[#1A2E22] focus:border-[#22C55E] outline-none pl-10 pr-4 py-3 text-sm text-white placeholder-[#5D7A6B] transition-colors"
                    data-testid="signin-email-input"
                    autoFocus
                    required
                  />
                </div>
              </div>

              {error && (
                <p className="text-[#FCA5A5] text-xs" data-testid="signin-error">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#16A34A] hover:bg-[#15803D] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium px-5 py-3 text-sm transition-colors flex items-center justify-center gap-2"
                data-testid="signin-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Sending link…</>
                ) : (
                  <>Send my access link</>
                )}
              </button>

              <p className="text-[11px] text-[#5D7A6B] leading-relaxed pt-2">
                Don't have an account yet?{" "}
                <Link to="/" className="text-[#22C55E] hover:underline inline-block py-2 -my-2" data-testid="signin-apply-link">
                  Apply for access →
                </Link>
              </p>
            </form>
          ) : (
            <div className="border border-[#1A2E22] bg-[#09120D] p-6" data-testid="signin-sent-state">
              <CheckCircle2 className="w-8 h-8 text-[#22C55E] mb-3" />
              <h2 className="text-lg font-semibold mb-2">Check your inbox.</h2>
              <p className="text-[#A1B2A8] text-sm leading-relaxed mb-4">
                If <span className="text-white">{email}</span> is registered with Delta GreenX,
                you'll receive your access link in the next minute. The email comes from{" "}
                <span className="text-white">Hello@deltagreenx.com</span> — check your spam folder
                if you don't see it.
              </p>
              <p className="text-[#A1B2A8] text-sm leading-relaxed mb-5">
                <span className="text-[#FBBF24]">If you haven't been approved yet</span>, you'll get
                an "under review" status email instead. We'll send the access link the moment
                admin approves your application.
              </p>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                className="text-sm text-[#22C55E] hover:underline"
                data-testid="signin-try-another"
              >
                Use a different email →
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
